import type { ReactElement } from 'react'
import { useAPPost } from '@/hooks/useAPPost'
import { QuotedPostEmbed } from '@/components/QuotedPostEmbed'
import { isSafeUrl } from '@/lib/url'

export interface FediverseEmbedProps {
    /** The original external link URL (human-facing fediverse post URL). */
    uri: string
    /** The generic link card to show while loading or on failure. */
    fallback: ReactElement
}

/**
 * Resolves a fediverse/ActivityPub post URL via `useAPPost` and renders it as a
 * quote-style card. While loading, on error, or when the link does not resolve to
 * an AP post, renders `fallback` (the normal external link card) so behavior is
 * never worse than today. Clicking the card opens the original post in a new tab.
 */
export function FediverseEmbed({ uri, fallback }: FediverseEmbedProps): ReactElement {
    const { post, isLoading, error } = useAPPost(uri)

    if (isLoading || error || !post) {
        return fallback
    }

    return (
        <QuotedPostEmbed
            post={post}
            onPostClick={() => {
                if (isSafeUrl(uri)) window.open(uri, '_blank', 'noopener,noreferrer')
            }}
        />
    )
}
